/**
 * src/pages/WhereDidWeComeFrom/sections/BecomingHumanSection.tsx
 * Section 5: "Becoming Human"
 * Interactive timeline and detail cards exploring human evolutionary history.
 * Emphasizes that human evolution is a branching bush rather than a single ladder,
 * integrating genomic admixture discoveries (Neanderthal and Denisovan DNA).
 */

import React, { useState } from "react";
import { GitBranch, Dna } from "lucide-react";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import { HOMININ_STAGES, type HomininStage } from "../whereDidWeComeFromData";
import "@/components/cosmic/cosmic.css";
import styles from "../WhereDidWeComeFrom.module.css";
export function BecomingHumanSection() {
  const [selectedStageId, setSelectedStageId] = useState<string>("h-sapiens");
  const currentStage: HomininStage = HOMININ_STAGES.find(s => s.id === selectedStageId) || HOMININ_STAGES[0];
  return <section id="becoming-human" className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 min-w-0" aria-label="Human Evolutionary Bush">
      <SectionHeading eyebrow="Paleoanthropology & Ancient DNA" title="Becoming Human: A Branching Bush, Not a Ladder" description="Human evolution was not a single, linear march from ape to human. It was a complex, braided bush of diverse lineages that overlapped in time and occasionally interbred." />

      {/* Key Insight Bar: Ancient Genomes & Branching Bushes */}
      <div className="ql-glass p-5 sm:p-6 mb-8 border border-primary/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Dna className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
            Paleogenomics has revolutionized our family tree. The sequencing of Neanderthal
            <Footnote n={8} /> and Denisovan genomes proved that modern humans outside Africa carry 1–2% Neanderthal DNA, while some Oceanian and Southeast Asian populations carry up to 4–6% Denisovan ancestry. Our ancestors shared the planet with multiple other human species for hundreds of millennia.
          </p>
        </div>
      </div>

      {/* Interactive Stage Selector and Deep Detail Display */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start min-w-0">
        {/* Left Column: Stage Selector List */}
        <div className="lg:col-span-5 flex flex-col gap-3 min-w-0" role="tablist" aria-label="Hominin Evolutionary Stages">
          {HOMININ_STAGES.map(stage => {
          const isSelected = stage.id === selectedStageId;
          return <button key={stage.id} type="button" role="tab" aria-selected={isSelected} aria-controls={`stage-detail-${stage.id}`} onClick={() => setSelectedStageId(stage.id)} className={`ql-card p-4 sm:p-5 text-left flex flex-col gap-1.5 transition-all min-w-0 ${styles.homininCard} ${isSelected ? "border-primary bg-card/90 shadow-[0_0_18px_var(--glow-primary)]" : "border-border/40 opacity-80 hover:opacity-100"}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-xs font-semibold text-primary">
                    {stage.timeWindow}
                  </span>
                  <EvidenceBadge level={stage.level} compact />
                </div>
                <div className="font-serif text-sm sm:text-base font-bold text-foreground">
                  {stage.species}
                </div>
                <div className="text-xs text-muted-foreground font-sans line-clamp-1">
                  {stage.significance}
                </div>
              </button>;
        })}
        </div>

        {/* Right Column: Detailed Fossil & Genomic Telemetry */}
        <div id={`stage-detail-${currentStage.id}`} role="tabpanel" className="lg:col-span-7 ql-glass-strong p-6 sm:p-8 rounded-xl border border-primary/40 flex flex-col gap-6 min-w-0">
          <div className="flex flex-col gap-2 border-b border-border/40 pb-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-mono text-xs sm:text-sm font-semibold text-primary px-3 py-1 rounded bg-primary/10 border border-primary/20">
                {currentStage.timeWindow}
              </span>
              <EvidenceBadge level={currentStage.level} />
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl font-extrabold text-foreground mt-2">
              {currentStage.species}
              {currentStage.refs && currentStage.refs.length > 0 && <span className="inline-flex items-center ml-1">
                  {currentStage.refs.map(r => <Footnote key={r} n={r} />)}
                </span>}
            </h3>
            <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">
              {currentStage.significance}
            </p>
          </div>

          {/* Key Anatomical & Archaeological Traits */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-xs font-mono text-primary font-semibold uppercase tracking-wider">
              <GitBranch className="size-4 text-primary" aria-hidden="true" />
              <span>Anatomical &amp; Cultural Markers</span>
            </div>

            <ul className="space-y-3 min-w-0 list-none p-0 m-0">
              {currentStage.traits.map((trait, idx) => <li key={idx} className="flex items-start gap-3 p-3.5 rounded-lg border border-border/40 bg-card/40 text-xs sm:text-sm text-muted-foreground leading-relaxed min-w-0">
                  <span className="size-1.5 rounded-full bg-primary shrink-0 mt-2" />
                  <span className="break-words font-sans">{trait}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}